import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  OutlinedInput,
  ListItemText,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CheckCircle,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { appsService, AppWithConnectionStatus, EnhancedAppsResponse } from '../../services/appsService';
import socketService from '../../services/socketService';
import { API_CONFIG, API_ENDPOINTS, GOOGLE_DRIVE_CLIENT_ID, GOOGLE_CALENDAR_CLIENT_ID, OUTLOOK_CALENDAR_CLIENT_ID, SHAREPOINT_CLIENT_ID, JIRA_CLIENT_ID, SOCKET_KEYS, STORAGE_KEYS, shouldShowAppInMarketplace, getNodeApiHeaders } from '../../helpers/constants';
import channelService from '../../services/channelService';
import OAuthConnectionDialog from '../oauth/OAuthConnectionDialog';
import BasicAuthConnectionDialog, { BasicAuthConnectionData } from '../oauth/BasicAuthConnectionDialog';
import OdooConnectionDialog, { OdooConnectionData } from '../oauth/OdooConnectionDialog';
import useUnifiedOAuth from '../../hooks/useUnifiedOAuth';
import OAUTH_FRONTEND_CONFIGS, { OAuthConnectionData } from '../../helpers/oauthConfig';
import { useChannelAdmin } from '../../hooks/useChannelAdmin';
import AppAccountManageModal from './AppAccountManageModal';
import { useToast } from '../../contexts/ToastContext';
import { encryptSecretForStorage } from '../../services/secretEncryptionService';

// Interface for Jira Project
interface JiraProject {
  id: string;
  key: string;
  name: string;
  projectTypeKey: string;
  simplified?: boolean;
  style?: string;
  isPrivate?: boolean;
}

interface AppsGridProps {
  channelId?: string;
  channelName?: string;
  isChannelArchived?: boolean;
  pageSize?: number;
  category?: string; // Optional category filter
  showSearch?: boolean; // Whether to show search bar
  showCategoryFilter?: boolean; // Whether to show category filter
  selectionMode?: boolean; // Whether this is for selection (like in DataSourceStep)
  selectedApp?: AppWithConnectionStatus | null; // Currently selected app
  onAppSelect?: (app: AppWithConnectionStatus) => void; // Callback for app selection
  connectingApps?: { [key: string]: boolean }; // External connecting state
  onConnect?: (appKey: string, appId: string) => void; // External connect handler
  height?: string; // Custom height for the grid container
  disableInternalScroll?: boolean; // Whether to disable internal scrolling (for use in modals)
  subCategory?: string; // Optional sub_category filter (e.g., "Datasource")
  filterIsActive?: boolean; // Whether to filter by is_active: true only
  refreshTrigger?: number; // Bump to re-fetch app-accounts for channel and merge into app list (e.g. after OAuth connection success)
  optimisticConnectedAppId?: string | null; // App id to show as connected immediately after OAuth success (before app-accounts fetch completes)
}

const getEncryptedClientSecret = (oauthData: any, provider: string): string => {
  const clientSecret = oauthData?.clientSecret;
  if (typeof clientSecret !== 'string' || !clientSecret.trim()) {
    throw new Error(`${provider} OAuth response did not include encrypted client secret`);
  }

  return clientSecret.trim();
};

// Apps are now controlled by backend is_active field
// is_active: true -> shows Connect/Disconnect button
// is_active: false -> shows Coming Soon button

const AppsGrid: React.FC<AppsGridProps> = ({ 
  channelId, 
  channelName, 
  isChannelArchived = false, 
  pageSize = 24,
  category: propCategory,
  showSearch = true,
  showCategoryFilter = true,
  selectionMode = false,
  selectedApp,
  onAppSelect,
  connectingApps: externalConnectingApps,
  onConnect: externalOnConnect,
  height,
  disableInternalScroll = false,
  subCategory,
  filterIsActive,
  refreshTrigger,
  optimisticConnectedAppId
}) => {
  const [appSearchTerm, setAppSearchTerm] = useState('');
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const [apps, setApps] = useState<AppWithConnectionStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectingApps, setConnectingApps] = useState<{ [key: string]: boolean }>({});
  const [disconnectingApps, setDisconnectingApps] = useState<{ [key: string]: boolean }>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(propCategory || '');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // App account manage modal: show connected account details (email, connected on, connected by)
  const [manageModalApp, setManageModalApp] = useState<AppWithConnectionStatus | null>(null);
  
  // Check if current user is channel admin
  const { isChannelAdmin } = useChannelAdmin(channelId);
  const { markSessionExpired } = useAuth();

  // Unified OAuth success handler
  const handleUnifiedOAuthSuccess = async (data: any) => {
    try {
      if (!pendingAppConnection) {
        console.error('No pending app connection found');
        return;
      }

      const { appKey, appId } = pendingAppConnection;
      

      // Get authentication token
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Create app account with connection_settings
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify({
          app_id: appId,
          channel_id: channelId,
          account_name: `${appKey}_${Date.now()}`,
          connection_settings: data.connection_settings
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create app account: ${response.status}`);
      }

      const result = await response.json();

      // Refresh apps list to show updated connection status
      await fetchApps(appSearchTerm, selectedCategory);
      
    } catch (error) {
      console.error('Error creating app account:', error);
      alert('Failed to complete connection. Please try again.');
    } finally {
      // Clear states
      setPendingAppConnection(null);
      setConnectingApps(prev => ({ ...prev, [pendingAppConnection?.appKey || '']: false }));
    }
  };

  // Unified OAuth error handler
  const handleUnifiedOAuthError = (error: string) => {
    console.error('Unified OAuth error:', error);
    alert(`Authentication failed: ${error}`);
    
    if (pendingAppConnection) {
      setConnectingApps(prev => ({ ...prev, [pendingAppConnection.appKey]: false }));
      setPendingAppConnection(null);
    }
  };
  
  // Initialize unified OAuth hook
  const unifiedOAuth = useUnifiedOAuth({
    channelId: channelId || '',
    onSuccess: handleUnifiedOAuthSuccess,
    onError: handleUnifiedOAuthError,
    onSessionExpired: markSessionExpired,
  });
  
  // Google Drive OAuth specific states
  const [googleDriveOauthData, setGoogleDriveOauthData] = useState<any>(null);
  const googleDrivePopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const googleDriveOAuthProcessedRef = useRef<boolean>(false);
  
  // Google Calendar OAuth specific states
  const [googleCalendarOauthData, setGoogleCalendarOauthData] = useState<any>(null);
  const googleCalendarPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const googleCalendarOAuthProcessedRef = useRef<boolean>(false);
  
  // Outlook Calendar OAuth specific states
  const [outlookCalendarOauthData, setOutlookCalendarOauthData] = useState<any>(null);
  const outlookCalendarPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const outlookCalendarOAuthProcessedRef = useRef<boolean>(false);
  
  // SharePoint OAuth specific states
  const [sharePointOauthData, setSharePointOauthData] = useState<any>(null);
  const sharePointPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sharePointOAuthProcessedRef = useRef<boolean>(false);
  // Per-provider cancel toast dedupe when this grid owns the popup listeners
  const appsGridGoogleDriveOAuthCancelledToastShownRef = useRef(false);
  const appsGridSharePointOAuthCancelledToastShownRef = useRef(false);
  // True after OAUTH_SUCCESS / OAUTH_CANCELLED / fallback-close — avoids duplicate “cancelled” toast
  const appsGridGoogleDriveOAuthPopupSettledRef = useRef(false);
  const appsGridSharePointOAuthPopupSettledRef = useRef(false);
  
  // Jira OAuth specific states
  const [jiraOauthData, setJiraOauthData] = useState<any>(null);
  const jiraPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const jiraOAuthProcessedRef = useRef<boolean>(false);
  
  // Unified OAuth states
  const [oauthDialogOpen, setOauthDialogOpen] = useState(false);
  const [selectedOAuthProvider, setSelectedOAuthProvider] = useState<string>('');
  const [pendingAppConnection, setPendingAppConnection] = useState<{ appKey: string; appId: string } | null>(null);
  
  // Basic Auth states (for Freshdesk)
  const [basicAuthDialogOpen, setBasicAuthDialogOpen] = useState(false);
  const [pendingBasicAuthConnection, setPendingBasicAuthConnection] = useState<{ appKey: string; appId: string; appName: string } | null>(null);
  // Odoo API-key connection dialog state
  const [odooDialogOpen, setOdooDialogOpen] = useState(false);
  const [pendingOdooConnection, setPendingOdooConnection] = useState<{ appKey: string; appId: string; appName: string } | null>(null);
  
  // Jira project selection dialog state
  const [jiraProjectDialogOpen, setJiraProjectDialogOpen] = useState(false);
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [selectedJiraProjects, setSelectedJiraProjects] = useState<JiraProject[]>([]);
  const [loadingJiraProjects, setLoadingJiraProjects] = useState(false);
  const [pendingJiraConnectionData, setPendingJiraConnectionData] = useState<any>(null);
  
  const hasFetchedApps = useRef(false);
  
  // Theme context
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const { showWarning } = useToast();

  // Use external connecting state if provided, otherwise use internal state
  const currentConnectingApps = externalConnectingApps || connectingApps;

  // Listen for Google Drive OAuth data from callback page
  // Skip processing if externalOnConnect is provided (DataSourceStep will handle it)
  useEffect(() => {
    // If externalOnConnect is provided, don't process OAuth here (let parent handle it)
    if (externalOnConnect) return;
    
    const handleGoogleDriveOAuthData = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'google_drive') {
        appsGridGoogleDriveOAuthPopupSettledRef.current = true;
        // Avoid processing the same OAuth result twice (e.g. duplicate postMessage or strict mode)
        if (googleDriveOAuthProcessedRef.current) return;
        googleDriveOAuthProcessedRef.current = true;
        try {
          setGoogleDriveOauthData(event.data.data);
          // Clear popup monitoring interval
          if (googleDrivePopupIntervalRef.current) {
            clearInterval(googleDrivePopupIntervalRef.current);
            googleDrivePopupIntervalRef.current = null;
          }
          
          // Clear loading state for Google Drive
          setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
          
          // Automatically create app account with OAuth data
          await handleGoogleDriveConnection(event.data.data);
        } finally {
          // Allow future OAuth flows to be processed after a short delay
          setTimeout(() => { googleDriveOAuthProcessedRef.current = false; }, 2000);
        }
      }
    };

    window.addEventListener('message', handleGoogleDriveOAuthData);
    return () => window.removeEventListener('message', handleGoogleDriveOAuthData);
  }, [channelId, externalOnConnect]);

  // Listen for Google Calendar OAuth data from callback page
  // Skip processing if externalOnConnect is provided (DataSourceStep will handle it)
  useEffect(() => {
    // If externalOnConnect is provided, don't process OAuth here (let parent handle it)
    if (externalOnConnect) return;
    
    const handleGoogleCalendarOAuthData = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'google_calendar') {
        // Avoid processing the same OAuth result twice (e.g. duplicate postMessage or strict mode)
        if (googleCalendarOAuthProcessedRef.current) return;
        googleCalendarOAuthProcessedRef.current = true;
        try {
          setGoogleCalendarOauthData(event.data.data);
          // Clear popup monitoring interval
          if (googleCalendarPopupIntervalRef.current) {
            clearInterval(googleCalendarPopupIntervalRef.current);
            googleCalendarPopupIntervalRef.current = null;
          }
          
          // Clear loading state for Google Calendar
          setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
          
          // Automatically create app account with OAuth data
          await handleGoogleCalendarConnection(event.data.data);
        } finally {
          // Allow future OAuth flows to be processed after a short delay
          setTimeout(() => { googleCalendarOAuthProcessedRef.current = false; }, 2000);
        }
      }
    };

    window.addEventListener('message', handleGoogleCalendarOAuthData);
    return () => window.removeEventListener('message', handleGoogleCalendarOAuthData);
  }, [channelId, externalOnConnect]);

  // Listen for Outlook Calendar OAuth data from callback page
  // Skip processing if externalOnConnect is provided (DataSourceStep will handle it)
  useEffect(() => {
    // If externalOnConnect is provided, don't process OAuth here (let parent handle it)
    if (externalOnConnect) return;
    
    const handleOutlookCalendarOAuthData = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'microsoft_outlook_calendar') {
        // Avoid processing the same OAuth result twice (e.g. duplicate postMessage or strict mode)
        if (outlookCalendarOAuthProcessedRef.current) return;
        outlookCalendarOAuthProcessedRef.current = true;
        try {
          setOutlookCalendarOauthData(event.data.data);
          // Clear popup monitoring interval
          if (outlookCalendarPopupIntervalRef.current) {
            clearInterval(outlookCalendarPopupIntervalRef.current);
            outlookCalendarPopupIntervalRef.current = null;
          }
          
          // Clear loading state for Outlook Calendar
          setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
          
          // Automatically create app account with OAuth data
          await handleOutlookCalendarConnection(event.data.data);
        } finally {
          // Allow future OAuth flows to be processed after a short delay
          setTimeout(() => { outlookCalendarOAuthProcessedRef.current = false; }, 2000);
        }
      }
    };

    window.addEventListener('message', handleOutlookCalendarOAuthData);
    return () => window.removeEventListener('message', handleOutlookCalendarOAuthData);
  }, [channelId, externalOnConnect]);

  // Listen for SharePoint OAuth data from callback page
  // Skip processing if externalOnConnect is provided (DataSourceStep will handle it)
  useEffect(() => {
    // If externalOnConnect is provided, don't process OAuth here (let parent handle it)
    if (externalOnConnect) return;
    
    const handleSharePointOAuthData = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'sharepoint') {
        appsGridSharePointOAuthPopupSettledRef.current = true;
        // Avoid processing the same OAuth result twice (e.g. duplicate postMessage or strict mode)
        if (sharePointOAuthProcessedRef.current) return;
        sharePointOAuthProcessedRef.current = true;
        try {
          setSharePointOauthData(event.data.data);
          // Clear popup monitoring interval
          if (sharePointPopupIntervalRef.current) {
            clearInterval(sharePointPopupIntervalRef.current);
            sharePointPopupIntervalRef.current = null;
          }
          
          // Clear loading state for SharePoint
          setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
          
          // Automatically create app account with OAuth data
          await handleSharePointConnection(event.data.data);
        } finally {
          // Allow future OAuth flows to be processed after a short delay
          setTimeout(() => { sharePointOAuthProcessedRef.current = false; }, 2000);
        }
      }
    };

    window.addEventListener('message', handleSharePointOAuthData);
    return () => window.removeEventListener('message', handleSharePointOAuthData);
  }, [channelId, externalOnConnect]);

  // User cancelled Google Drive / SharePoint OAuth in popup — only when this grid listens (no externalOnConnect)
  useEffect(() => {
    if (externalOnConnect) return;

    const handleCancelled = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const d = event.data;
      if (!d || d.type !== 'OAUTH_CANCELLED') return;
      const key =
        d.provider === 'google_drive'
          ? 'google_drive'
          : d.provider === 'sharepoint'
            ? 'sharepoint'
            : null;
      if (!key) return;
      if (key === 'google_drive') {
        appsGridGoogleDriveOAuthPopupSettledRef.current = true;
      }
      if (key === 'sharepoint') {
        appsGridSharePointOAuthPopupSettledRef.current = true;
      }
      if (key === 'google_drive' && googleDrivePopupIntervalRef.current) {
        clearInterval(googleDrivePopupIntervalRef.current);
        googleDrivePopupIntervalRef.current = null;
      }
      if (key === 'sharepoint' && sharePointPopupIntervalRef.current) {
        clearInterval(sharePointPopupIntervalRef.current);
        sharePointPopupIntervalRef.current = null;
      }
      setConnectingApps((prev) => ({ ...prev, [key]: false }));
      const toastRef =
        key === 'google_drive'
          ? appsGridGoogleDriveOAuthCancelledToastShownRef
          : appsGridSharePointOAuthCancelledToastShownRef;
      if (!toastRef.current) {
        toastRef.current = true;
        showWarning('Authentication cancelled by user');
      }
    };

    window.addEventListener('message', handleCancelled);
    return () => window.removeEventListener('message', handleCancelled);
  }, [externalOnConnect, showWarning]);

  // Listen for Jira OAuth data from callback page
  // Skip processing if externalOnConnect is provided (DataSourceStep will handle it)
  useEffect(() => {
    // If externalOnConnect is provided, don't process OAuth here (let parent handle it)
    if (externalOnConnect) return;
    
    const handleJiraOAuthData = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'jira') {
        // Avoid processing the same OAuth result twice (e.g. duplicate postMessage or strict mode)
        if (jiraOAuthProcessedRef.current) return;
        jiraOAuthProcessedRef.current = true;
        try {
          setJiraOauthData(event.data.data);
          // Clear popup monitoring interval
          if (jiraPopupIntervalRef.current) {
            clearInterval(jiraPopupIntervalRef.current);
            jiraPopupIntervalRef.current = null;
          }
          
          // Clear loading state for Jira
          setConnectingApps(prev => ({ ...prev, ['jira']: false }));
          
          // Automatically create app account with OAuth data
          await handleJiraConnection(event.data.data);
        } finally {
          // Allow future OAuth flows to be processed after a short delay
          setTimeout(() => { jiraOAuthProcessedRef.current = false; }, 2000);
        }
      }
    };

    window.addEventListener('message', handleJiraOAuthData);
    return () => window.removeEventListener('message', handleJiraOAuthData);
  }, [channelId, externalOnConnect]);

  // Listen for OAuth data requests from popup (fallback mechanism)
  useEffect(() => {
    const handleOAuthDataRequest = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'REQUEST_OAUTH_DATA') {
        const requestedState = event.data.state;
        
        // Try to find the data in localStorage
        let dataToSend = localStorage.getItem('oauth_temp_data');
        if (!dataToSend && requestedState) {
          dataToSend = localStorage.getItem(`oauth_${requestedState}`);
        }
        
        // Send data back to popup
        if (event.source && dataToSend) {
          (event.source as Window).postMessage({
            type: 'OAUTH_DATA_RESPONSE',
            state: requestedState,
            data: dataToSend
          }, window.location.origin);
        } else {
          console.warn('⚠️ No OAuth data found to send to popup');
        }
      }
    };

    window.addEventListener('message', handleOAuthDataRequest);
    return () => window.removeEventListener('message', handleOAuthDataRequest);
  }, []);

  // Update the existing cleanup useEffect to include Google Drive, Google Calendar, Outlook Calendar, SharePoint, and Jira
  useEffect(() => {
    return () => {
      // Clear popup monitoring intervals
      if (googleDrivePopupIntervalRef.current) {
        clearInterval(googleDrivePopupIntervalRef.current);
      }
      if (googleCalendarPopupIntervalRef.current) {
        clearInterval(googleCalendarPopupIntervalRef.current);
      }
      if (outlookCalendarPopupIntervalRef.current) {
        clearInterval(outlookCalendarPopupIntervalRef.current);
      }
      if (sharePointPopupIntervalRef.current) {
        clearInterval(sharePointPopupIntervalRef.current);
      }
      if (jiraPopupIntervalRef.current) {
        clearInterval(jiraPopupIntervalRef.current);
      }
    };
  }, []);

  // Handle Google Drive OAuth flow
  const handleGoogleDriveOAuth = (appId: string) => {
    try {
      appsGridGoogleDriveOAuthCancelledToastShownRef.current = false;
      appsGridGoogleDriveOAuthPopupSettledRef.current = false;
      // Set loading state for Google Drive
      setConnectingApps(prev => ({ ...prev, ['google_drive']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/googledrive/callback`;
      
      // Google Drive specific scopes
      const scope = [
        'https://www.googleapis.com/auth/drive.readonly',
        'https://www.googleapis.com/auth/userinfo.profile',
        'https://www.googleapis.com/auth/userinfo.email',
        'openid'
      ].join(' ');
      
      const responseType = 'code';
      const accessType = 'offline';
      const prompt = 'consent';
      
      // Use the same Gmail client ID (it works for all Google services)
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${GOOGLE_DRIVE_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=${responseType}&` +
        `access_type=${accessType}&` +
        `prompt=${prompt}&` + 
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(googleAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (googleDrivePopupIntervalRef.current) {
        clearInterval(googleDrivePopupIntervalRef.current);
      }
      
      // Monitor popup closure
      googleDrivePopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(googleDrivePopupIntervalRef.current!);
          googleDrivePopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
          // Fallback if opener/postMessage failed (toast is behind drawer without high Toaster z-index)
          window.setTimeout(() => {
            if (!appsGridGoogleDriveOAuthPopupSettledRef.current) {
              appsGridGoogleDriveOAuthPopupSettledRef.current = true;
              if (!appsGridGoogleDriveOAuthCancelledToastShownRef.current) {
                appsGridGoogleDriveOAuthCancelledToastShownRef.current = true;
                showWarning('Authentication cancelled by user');
              }
            }
          }, 500);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating Google Drive OAuth:', error);
      setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
    }
  };

  // Handle Google Calendar OAuth flow
  const handleGoogleCalendarOAuth = (appId: string) => {
    try {
      // Reset so the message listener will process the upcoming OAuth callback
      googleCalendarOAuthProcessedRef.current = false;
      // Set loading state for Google Calendar
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/googlecalendar/callback`;
      
      // Google Calendar specific scopes
      // https://www.googleapis.com/auth/calendar - Full read/write access to calendars and events
      // https://www.googleapis.com/auth/calendar.events - Read/write access to events only
      // https://www.googleapis.com/auth/calendar.readonly - Read-only access to calendars and events
      // https://www.googleapis.com/auth/calendar.events.readonly - Read-only access to events only
      const scope = [
        'https://www.googleapis.com/auth/calendar', // Full access: view, read, write, and modify calendars and events
        'https://www.googleapis.com/auth/userinfo.profile',
        'https://www.googleapis.com/auth/userinfo.email',
        'openid'
      ].join(' ');
      
      const responseType = 'code';
      const accessType = 'offline';
      const prompt = 'consent';
      
      // Use the Google Calendar client ID
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${GOOGLE_CALENDAR_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=${responseType}&` +
        `access_type=${accessType}&` +
        `prompt=${prompt}&` + 
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(googleAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (googleCalendarPopupIntervalRef.current) {
        clearInterval(googleCalendarPopupIntervalRef.current);
      }
      
      // Monitor popup closure
      googleCalendarPopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(googleCalendarPopupIntervalRef.current!);
          googleCalendarPopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating Google Calendar OAuth:', error);
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
    }
  };

  // Handle Outlook Calendar OAuth flow
  const handleOutlookCalendarOAuth = (appId: string) => {
    try {
      // Set loading state for Outlook Calendar
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/outlookcalendar/callback`;
      
      // Outlook Calendar specific scopes
      const scope = [
        'https://graph.microsoft.com/Calendars.Read',
        'https://graph.microsoft.com/Calendars.ReadWrite',
        'https://graph.microsoft.com/User.Read',
        'openid',
        'offline_access'
      ].join(' ');
      
      const responseType = 'code';
      const responseMode = 'query';
      
      // Use the Outlook Calendar client ID
      const outlookAuthUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${OUTLOOK_CALENDAR_CLIENT_ID}&` +
        `response_type=${responseType}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_mode=${responseMode}&` +
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(outlookAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (outlookCalendarPopupIntervalRef.current) {
        clearInterval(outlookCalendarPopupIntervalRef.current);
      }
      
      // Monitor popup closure
      outlookCalendarPopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(outlookCalendarPopupIntervalRef.current!);
          outlookCalendarPopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating Outlook Calendar OAuth:', error);
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
    }
  };

  // Handle Google Calendar connection after OAuth success
  const handleGoogleCalendarConnection = async (oauthData: any) => {
    // Prevent duplicate API calls - check if already connecting
    if (connectingApps['google_calendar']) {
      console.warn('Google Calendar connection already in progress, skipping duplicate call');
      return;
    }
    
    try {
      if (!channelId) {
        throw new Error('Channel ID is required');
      }
      
      // Set connecting state to prevent duplicate calls
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: true }));
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'google_calendar',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        email_id: oauthData.email_id,
        connection_method: 'oauth',
        oauth_provider: 'google',
        client_id: oauthData.clientId || GOOGLE_CALENDAR_CLIENT_ID,
        client_secret: getEncryptedClientSecret(oauthData, 'Google Calendar')
      };
      
      const requestBody = {
        app_id: oauthData.app_id,
        channel_id: channelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      
      // Refresh the apps list to show updated connection status
      await fetchApps(appSearchTerm, selectedCategory);
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: channelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }
      
      // Clear OAuth data after successful connection
      setGoogleCalendarOauthData(null);
      
      // Clear connecting state after successful connection
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
      
    } catch (error) {
      console.error('Error creating Google Calendar connection:', error);
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
    }
  };

  // Handle Outlook Calendar connection after OAuth success
  const handleOutlookCalendarConnection = async (oauthData: any) => {
    // Prevent duplicate API calls - check if already connecting
    if (connectingApps['microsoft_outlook_calendar']) {
      console.warn('Outlook Calendar connection already in progress, skipping duplicate call');
      return;
    }
    
    try {
      if (!channelId) {
        throw new Error('Channel ID is required');
      }
      
      // Set connecting state to prevent duplicate calls
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: true }));
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'microsoft_outlook_calendar',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        email_id: oauthData.email_id,
        connection_method: 'oauth',
        oauth_provider: 'microsoft',
        client_id: oauthData.clientId || OUTLOOK_CALENDAR_CLIENT_ID,
        client_secret: getEncryptedClientSecret(oauthData, 'Outlook Calendar')
      };
      
      const requestBody = {
        app_id: oauthData.app_id,
        channel_id: channelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      
      // Refresh the apps list to show updated connection status
      await fetchApps(appSearchTerm, selectedCategory);
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: channelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }
      
      // Clear OAuth data after successful connection
      setOutlookCalendarOauthData(null);
      
      // Clear connecting state after successful connection
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
      
    } catch (error) {
      console.error('Error creating Outlook Calendar connection:', error);
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
    }
  };

  // Handle SharePoint OAuth flow
  const handleSharePointOAuth = (appId: string) => {
    try {
      // Reset so the message listener will process the upcoming OAuth callback
      sharePointOAuthProcessedRef.current = false;
      appsGridSharePointOAuthCancelledToastShownRef.current = false;
      appsGridSharePointOAuthPopupSettledRef.current = false;
      // Set loading state for SharePoint
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/sharepoint/callback`;
      
      // SharePoint specific scopes
      const scope = [
        'https://graph.microsoft.com/Files.ReadWrite.All',
        'https://graph.microsoft.com/Sites.FullControl.All',
        'https://graph.microsoft.com/Sites.Manage.All',
        'https://graph.microsoft.com/SharePointCrossTenantMigration.Manage.All',
        'https://graph.microsoft.com/SharePointTenantSettings.ReadWrite.All',
        'https://graph.microsoft.com/User.Read',
        'https://graph.microsoft.com/Organization.Read.All',
        'offline_access'
      ].join(' ');
      
      const responseType = 'code';
      const responseMode = 'query';
      const accessType = 'offline';
      const prompt = 'consent';
      
      // Build Microsoft OAuth URL
      const microsoftAuthUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${SHAREPOINT_CLIENT_ID}&` +
        `response_type=${responseType}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_mode=${responseMode}&` +
        `access_type=${accessType}&` +
        `prompt=${prompt}&` + 
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(microsoftAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (sharePointPopupIntervalRef.current) {
        clearInterval(sharePointPopupIntervalRef.current);
      }
      
      // Monitor popup closure
      sharePointPopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(sharePointPopupIntervalRef.current!);
          sharePointPopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
          window.setTimeout(() => {
            if (!appsGridSharePointOAuthPopupSettledRef.current) {
              appsGridSharePointOAuthPopupSettledRef.current = true;
              if (!appsGridSharePointOAuthCancelledToastShownRef.current) {
                appsGridSharePointOAuthCancelledToastShownRef.current = true;
                showWarning('Authentication cancelled by user');
              }
            }
          }, 500);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating SharePoint OAuth:', error);
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
    }
  };

  // Handle SharePoint connection after OAuth success
  const handleSharePointConnection = async (oauthData: any) => {
    // Prevent duplicate API calls - check if already connecting
    if (connectingApps['sharepoint']) {
      console.warn('SharePoint connection already in progress, skipping duplicate call');
      return;
    }
    
    try {
      if (!channelId) {
        throw new Error('Channel ID is required');
      }
      
      // Set connecting state to prevent duplicate calls
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: true }));
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'sharepoint',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        email_id: oauthData.email_id,
        tenant_id: oauthData.tenant_id,
        connection_method: 'oauth',
        oauth_provider: 'microsoft',
        client_id: oauthData.clientId || SHAREPOINT_CLIENT_ID,
        client_secret: getEncryptedClientSecret(oauthData, 'SharePoint')
      };
      
      const requestBody = {
        app_id: oauthData.app_id,
        channel_id: channelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      
      // Refresh the apps list to show updated connection status
      await fetchApps(appSearchTerm, selectedCategory);
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: channelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }
      
      // Clear OAuth data after successful connection
      setSharePointOauthData(null);
      
    } catch (error) {
      console.error('Error creating SharePoint connection:', error);
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
    }
  };

  // Handle Google Drive connection after OAuth success
  const handleGoogleDriveConnection = async (oauthData: any) => {
    // Prevent duplicate API calls - check if already connecting
    if (connectingApps['google_drive']) {
      console.warn('Google Drive connection already in progress, skipping duplicate call');
      return;
    }
    
    try {
      if (!channelId) {
        throw new Error('Channel ID is required');
      }
      
      // Set connecting state to prevent duplicate calls
      setConnectingApps(prev => ({ ...prev, ['google_drive']: true }));
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'google_drive',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        email_id: oauthData.email_id,
        connection_method: 'oauth',
        oauth_provider: 'google'
      };
      
      const requestBody = {
        app_id: oauthData.app_id,
        channel_id: channelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      
      // Refresh the apps list to show updated connection status
      await fetchApps(appSearchTerm, selectedCategory);
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: channelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }
      
      // Clear OAuth data after successful connection
      setGoogleDriveOauthData(null);
      
      // Clear connecting state after successful connection
      setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
      
    } catch (error) {
      console.error('Error creating Google Drive connection:', error);
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
    }
  };

  // Handle Jira OAuth flow
  const handleJiraOAuth = (appId: string) => {
    try {
      // Reset so the message listener will process the upcoming OAuth callback
      jiraOAuthProcessedRef.current = false;
      // Set loading state for Jira
      setConnectingApps(prev => ({ ...prev, ['jira']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/jira/callback`;
      
      // Jira OAuth 2.0 (3LO) specific parameters
      const audience = 'api.atlassian.com';
      const scope = [
        'read:me',
        'read:jira-work',
        'write:jira-work',
        'report:personal-data',
        'offline_access'
      ].join(' ');
      
      const responseType = 'code';
      const prompt = 'consent';
      
      // Jira OAuth URL
      const jiraAuthUrl = `https://auth.atlassian.com/authorize?` +
        `audience=${encodeURIComponent(audience)}&` +
        `client_id=${JIRA_CLIENT_ID}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=${responseType}&` +
        `prompt=${prompt}&` + 
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(jiraAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (jiraPopupIntervalRef.current) {
        clearInterval(jiraPopupIntervalRef.current);
      }
      
      // Monitor popup closure
      jiraPopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(jiraPopupIntervalRef.current!);
          jiraPopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['jira']: false }));
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating Jira OAuth:', error);
      setConnectingApps(prev => ({ ...prev, ['jira']: false }));
    }
  };

  // Refresh Jira access token
  const refreshJiraToken = async (refreshToken: string): Promise<any> => {
    try {
      const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/jira/refreshToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getNodeApiHeaders(),
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token refresh failed: ${response.status} ${errorText}`);
      }

      const tokenData = await response.json();
      return tokenData;
    } catch (error) {
      console.error('Error refreshing Jira token:', error);
      throw error;
    }
  };

  // Fetch Jira projects from backend with automatic token refresh on 401
  const fetchJiraProjects = async (accessToken: string, cloudId: string, refreshToken?: string, oauthDataRef?: any): Promise<JiraProject[]> => {
    try {
      const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/jira/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getNodeApiHeaders(),
        },
        body: JSON.stringify({
          access_token: accessToken,
          cloud_id: cloudId
        })
      });

      // If token expired (401), try to refresh and retry
      if (response.status === 401 && refreshToken && oauthDataRef) {
        try {
          const newTokenData = await refreshJiraToken(refreshToken);
          
          // Update the OAuth data with new tokens
          oauthDataRef.access_token = newTokenData.access_token;
          if (newTokenData.refresh_token) {
            oauthDataRef.refresh_token = newTokenData.refresh_token;
          }
          if (newTokenData.expires_in) {
            oauthDataRef.expires_in = newTokenData.expires_in;
          }
          if (newTokenData.scope) {
            oauthDataRef.scope = newTokenData.scope;
          }
          
          
          // Retry with new access token
          const retryResponse = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/jira/projects`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getNodeApiHeaders(),
            },
            body: JSON.stringify({
              access_token: newTokenData.access_token,
              cloud_id: cloudId
            })
          });

          if (!retryResponse.ok) {
            const errorText = await retryResponse.text();
            throw new Error(`HTTP error! status: ${retryResponse.status}, body: ${errorText}`);
          }

          const retryData = await retryResponse.json();
          return retryData.data?.projects || [];
        } catch (refreshError) {
          console.error('Error refreshing token:', refreshError);
          throw new Error('Failed to refresh access token. Please reconnect Jira.');
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      return data.data?.projects || [];
    } catch (error) {
      console.error('Error fetching Jira projects:', error);
      throw error;
    }
  };

  // Handle Jira connection after OAuth success
  const handleJiraConnection = async (oauthData: any) => {
    try {
      if (!channelId) {
        throw new Error('Channel ID is required');
      }
      
      // Store OAuth data temporarily
      setPendingJiraConnectionData(oauthData);
      
      // Fetch Jira projects
      setLoadingJiraProjects(true);
      try {
        // Pass refresh token and oauthData reference for automatic token refresh
        const projects = await fetchJiraProjects(
          oauthData.access_token, 
          oauthData.cloud_id,
          oauthData.refresh_token,
          oauthData // Pass reference to update tokens if refreshed
        );
        setJiraProjects(projects);
        setSelectedJiraProjects([]); // Reset selection
        setJiraProjectDialogOpen(true);
        // Update pending connection data with potentially refreshed tokens
        setPendingJiraConnectionData(oauthData);
      } catch (error) {
        console.error('Error fetching Jira projects:', error);
        // If fetching projects fails, proceed without project selection
        // You can show an error message or proceed directly
        await createJiraAppAccount(oauthData, []);
      } finally {
        setLoadingJiraProjects(false);
      }
      
    } catch (error) {
      console.error('Error in Jira connection flow:', error);
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['jira']: false }));
      setJiraOauthData(null);
    }
  };

  // Create or update Jira app account with selected projects
  const createJiraAppAccount = async (oauthData: any, selectedProjects: JiraProject[]) => {
    try {
      if (!channelId) {
        throw new Error('Channel ID is required');
      }
      
      const token = localStorage.getItem('refreshToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'jira',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        cloud_id: oauthData.cloud_id, // Jira specific
        site_url: oauthData.site_url, // Jira specific
        connection_method: 'oauth',
        oauth_provider: 'atlassian',
        selected_projects: selectedProjects // Add selected projects as full objects
      };
      
      // If updating existing connection, use PUT with only connection_settings
      // Otherwise use POST with full request body
      let url: string;
      let method: string;
      let requestBody: any;
      
      if (oauthData.connectionId) {
        // Update existing connection - only send connection_settings
        method = 'PUT';
        url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${oauthData.connectionId}`;
        requestBody = {
          connection_settings: connectionSettings
        };
      } else {
        // Create new connection - send full request body
        method = 'POST';
        url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`;
        requestBody = {
          app_id: oauthData.app_id,
          channel_id: channelId,
          connection_settings: connectionSettings
        };
      }
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      
      // Refresh the apps list to show updated connection status
      await fetchApps(appSearchTerm, selectedCategory);
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: channelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }
      
      // Clear OAuth data after successful connection
      setJiraOauthData(null);
      setPendingJiraConnectionData(null);
      
    } catch (error) {
      console.error('Error creating Jira connection:', error);
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['jira']: false }));
      throw error;
    }
  };

  // Handle project selection confirmation
  const handleJiraProjectSelectionConfirm = async () => {
    if (!pendingJiraConnectionData) {
      return;
    }
    
    try {
      setLoadingJiraProjects(true);
      await createJiraAppAccount(pendingJiraConnectionData, selectedJiraProjects);
      setJiraProjectDialogOpen(false);
      setSelectedJiraProjects([]);
      setJiraProjects([]);
    } catch (error) {
      console.error('Error confirming project selection:', error);
      // Keep dialog open on error so user can retry
    } finally {
      setLoadingJiraProjects(false);
    }
  };

  // Handle project selection dialog cancel
  const handleJiraProjectSelectionCancel = () => {
    setJiraProjectDialogOpen(false);
    setSelectedJiraProjects([]);
    setJiraProjects([]);
    setPendingJiraConnectionData(null);
    // Clear loading state
    setConnectingApps(prev => ({ ...prev, ['jira']: false }));
    setJiraOauthData(null);
  };

  // Handle clicking on project count to edit projects
  const handleEditJiraProjects = async (app: AppWithConnectionStatus) => {
    if (!app.connectionDetails) {
      return;
    }

    try {
      setLoadingJiraProjects(true);
      
      // Parse connection settings to get tokens and existing projects
      let connectionSettings: any;
      try {
        connectionSettings = typeof app.connectionDetails.connection_settings === 'string' 
          ? JSON.parse(app.connectionDetails.connection_settings)
          : app.connectionDetails.connection_settings;
      } catch (error) {
        console.error('Error parsing connection settings:', error);
        return;
      }

      const accessToken = connectionSettings?.access_token;
      const refreshToken = connectionSettings?.refresh_token;
      const cloudId = connectionSettings?.cloud_id;
      const existingProjects = connectionSettings?.selected_projects || [];

      if (!accessToken || !cloudId) {
        console.error('Missing required connection data');
        return;
      }

      // Fetch current projects
      const oauthData = {
        access_token: accessToken,
        refresh_token: refreshToken,
        cloud_id: cloudId,
        app_id: app.connectionDetails.app_id,
        expires_in: connectionSettings?.expires_in,
        token_type: connectionSettings?.token_type,
        scope: connectionSettings?.scope,
        site_url: connectionSettings?.site_url
      };

      const projects = await fetchJiraProjects(
        accessToken,
        cloudId,
        refreshToken,
        oauthData
      );

      setJiraProjects(projects);
      
      // Pre-select existing projects by matching IDs
      const existingProjectIds = existingProjects.map((p: JiraProject) => p.id);
      const preSelectedProjects = projects.filter((p: JiraProject) => 
        existingProjectIds.includes(p.id)
      );
      setSelectedJiraProjects(preSelectedProjects);
      
      // Store connection data for updating
      setPendingJiraConnectionData({
        ...oauthData,
        connectionId: app.connectionDetails.id // Store connection ID for update
      });
      
      setJiraProjectDialogOpen(true);
    } catch (error) {
      console.error('Error fetching projects for edit:', error);
    } finally {
      setLoadingJiraProjects(false);
    }
  };

  // Fetch categories from API
  const fetchCategories = React.useCallback(async () => {
    try {
      const response = await appsService.getCategories();
      setCategories(response.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  // Fetch all apps from API - fetch all pages sequentially to get everything
  const fetchApps = React.useCallback(async (searchTerm?: string, category?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use a reasonable page size that the API accepts
      const pageSize = 100;
      let allApps: AppWithConnectionStatus[] = [];
      let currentPage = 1;
      let hasMore = true;
      
      // Fetch all pages sequentially
      while (hasMore) {
        const response = searchTerm && searchTerm.trim() 
          ? await appsService.searchApps(searchTerm, currentPage, pageSize, channelId, category, subCategory, filterIsActive)
          : await appsService.getApps(currentPage, pageSize, channelId, category, subCategory, filterIsActive);
        
        if (response.apps && response.apps.length > 0) {
          allApps = [...allApps, ...response.apps];
        }
        
        // Check if there are more pages
        hasMore = currentPage < response.pages && response.apps && response.apps.length === pageSize;
        currentPage++;
      }
      
      // Filter by marketplace image type (gmail vs outlook) then set apps
      const filteredAppsByImageType = allApps.filter((app) => shouldShowAppInMarketplace(app.app_key));
      setApps(filteredAppsByImageType);
    } catch (err) {
      setError('Failed to load apps. Please try again.');
      console.error('Error fetching apps:', err);
    } finally {
      setLoading(false);
    }
  }, [channelId, subCategory, filterIsActive]);

  // When subCategory or filterIsActive is used, backend already filters, so use apps as-is
  const baseFilteredApps = apps;

  // Sort apps: is_active: true apps first, then is_active: false apps, both sorted by name ascending
  const filteredApps = [...baseFilteredApps].sort((a, b) => {
    // If both have same is_active status, sort by name ascending
    if (a.is_active === b.is_active) {
      return a.app_name.localeCompare(b.app_name, undefined, { sensitivity: 'base' });
    }
    
    // Active apps (is_active: true) come first
    return a.is_active ? -1 : 1;
  });

  // Disconnect app function
  const handleDisconnect = async (app: AppWithConnectionStatus) => {
    try {
      if (!app.connectionDetails) {
        return;
      }

      // Set disconnecting state for this specific app
      setDisconnectingApps(prev => ({ ...prev, [app.app_key]: true }));

      // Call the disconnect API
      await appsService.disconnectApp(app.connectionDetails.id);
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: channelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }
      
      // Refresh the apps list to update connection status
      setApps([]); // Clear existing apps
      await fetchApps(appSearchTerm, selectedCategory);
      
    } catch (error) {
      // You can add error notifications here
    } finally {
      // Clear disconnecting state for this specific app
      setDisconnectingApps(prev => ({ ...prev, [app.app_key]: false }));
    }
  };

  // Load categories on component mount
  useEffect(() => {
    if (showCategoryFilter) {
      fetchCategories();
    }
  }, [fetchCategories, showCategoryFilter]);

  // Reset fetch flag when channelId changes
  useEffect(() => {
    hasFetchedApps.current = false;
  }, [channelId]);

  // When parent bumps refreshTrigger after OAuth success: fetch app-accounts for channel only and merge into list
  useEffect(() => {
    if (!channelId || refreshTrigger === undefined || refreshTrigger <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const accounts = await appsService.getAppAccountsForChannel(channelId);
        if (cancelled) return;
        const byAppId = new Map(accounts.map((a) => [a.app_id, a]));
        setApps((prev) =>
          prev.map((app) => {
            const conn = byAppId.get(app.id);
            return { ...app, isConnected: !!conn, connectionDetails: conn };
          })
        );
      } catch (e) {
        if (!cancelled) console.error('Failed to refresh app connection status:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [channelId, refreshTrigger]);

  // Consolidated effect for initial load and debounced search/category changes.
  // Kept independent of refreshTrigger so search/category still trigger full fetch after OAuth.
  useEffect(() => {
    if (!channelId) {
      return;
    }

    // For initial load, fetch immediately
    if (!hasFetchedApps.current) {
      hasFetchedApps.current = true;
      fetchApps(appSearchTerm, selectedCategory);
      return;
    }

    // For subsequent changes (search/category), debounce
    const timeoutId = setTimeout(() => {
      setApps([]); // Clear existing apps
      fetchApps(appSearchTerm, selectedCategory);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [appSearchTerm, selectedCategory, channelId, fetchApps]);

  // Connect function using the same logic as openAnalyticsPanel
  const handleConnect = async (appKey: string, appId: string) => {
    try {
      // Use external connect handler if provided
      if (externalOnConnect) {
        externalOnConnect(appKey, appId);
        return;
      }

      // Set loading state for this specific app
      setConnectingApps(prev => ({ ...prev, [appKey]: true }));
      
      // Check if this app supports unified OAuth
      if (OAUTH_FRONTEND_CONFIGS[appKey]) {
        setSelectedOAuthProvider(appKey);
        setPendingAppConnection({ appKey, appId });
        setOauthDialogOpen(true);
        return;
      }
      
      // Check if this is Google Drive - use legacy OAuth
      if (appKey === 'google_drive') {
        handleGoogleDriveOAuth(appId);
        return; // Exit early for Google Drive
      }
      
      // Check if this is Google Calendar - use legacy OAuth
      if (appKey === 'google_calendar') {
        handleGoogleCalendarOAuth(appId);
        return; // Exit early for Google Calendar
      }
      
      // Check if this is Outlook Calendar - use legacy OAuth
      if (appKey === 'microsoft_outlook_calendar') {
        handleOutlookCalendarOAuth(appId);
        return; // Exit early for Outlook Calendar
      }
      
      // Check if this is SharePoint - use legacy OAuth
      if (appKey === 'sharepoint') {
        handleSharePointOAuth(appId);
        return; // Exit early for SharePoint
      }
      
      // Check if this is Jira - use legacy OAuth
      if (appKey === 'jira') {
        handleJiraOAuth(appId);
        return; // Exit early for Jira
      }
      
      // Check if this is Freshdesk - use basic auth (domain + API key)
      if (appKey === 'freshdesk') {
        const app = apps.find(a => a.app_key === appKey);
        setPendingBasicAuthConnection({ 
          appKey, 
          appId, 
          appName: app?.app_name || 'Freshdesk' 
        });
        setBasicAuthDialogOpen(true);
        return; // Exit early for Freshdesk
      }
      // Check if this is Odoo - use credentials form (URL + DB + username + API key)
      if (appKey === 'odoo') {
        const app = apps.find(a => a.app_key === appKey);
        setPendingOdooConnection({
          appKey,
          appId,
          appName: app?.app_name || 'Odoo',
        });
        setOdooDialogOpen(true);
        return;
      }
        
      // For other apps, show an error message since third-party integration has been removed
      console.error(`App connection not supported: ${appKey}. Third-party integration has been removed.`);
      alert(`Connection for ${appKey} is not currently supported. Please contact support for assistance.`);
      return;
    } catch (error) {
      console.error('Error in handleConnect:', error);
      alert('Failed to initiate connection. Please try again.');
    } finally {
      // Keep connecting true for popup/dialog flows until postMessage or user dismisses
      const keepConnectingUntilFlowEnds = new Set([
        'google_drive',
        'sharepoint',
        'google_calendar',
        'microsoft_outlook_calendar',
        'jira',
        'freshdesk',
        'odoo',
      ]);
      if (
        !OAUTH_FRONTEND_CONFIGS[appKey] &&
        !keepConnectingUntilFlowEnds.has(appKey)
      ) {
        setConnectingApps((prev) => ({ ...prev, [appKey]: false }));
      }
    }
  };

  // Handle app selection (for selection mode)
  const handleSelectApp = (app: AppWithConnectionStatus) => {
    if (selectionMode && onAppSelect) {
      onAppSelect(app);
    }
  };

  // Handle OAuth dialog close
  const handleOAuthDialogClose = () => {
    setOauthDialogOpen(false);
    setSelectedOAuthProvider('');
    
    // Clear loading state if connection was cancelled
    if (pendingAppConnection) {
      setConnectingApps(prev => ({ ...prev, [pendingAppConnection.appKey]: false }));
      setPendingAppConnection(null);
    }
  };

  // Handle OAuth connection from dialog
  const handleOAuthConnect = async (connectionData: OAuthConnectionData) => {
    if (!pendingAppConnection) {
      console.error('No pending app connection found');
      return;
    }

    try {

      // Close the dialog
      setOauthDialogOpen(false);

      // Initiate OAuth flow
      await unifiedOAuth.initiateOAuth(connectionData, pendingAppConnection.appId);
      
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      alert('Failed to start authentication. Please try again.');
      
      // Clear states on error
      setConnectingApps(prev => ({ ...prev, [pendingAppConnection.appKey]: false }));
      setPendingAppConnection(null);
    }
  };

  // Handle Basic Auth connection (for Freshdesk)
  const handleBasicAuthConnect = async (connectionData: BasicAuthConnectionData) => {
    if (!pendingBasicAuthConnection) {
      console.error('No pending basic auth connection found');
      return;
    }

    try {
      const { appKey, appId } = pendingBasicAuthConnection;

      // Get authentication token
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }

      const encryptedApiKey = await encryptSecretForStorage(connectionData.apiKey);

      // Build connection settings for basic auth
      // Freshdesk uses basic auth with domain and API key
      const connectionSettings = {
        domain: connectionData.domain,
        api_key: encryptedApiKey,
        auth_type: 'basic'
      };

      // Create app account with connection_settings
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify({
          app_id: appId,
          channel_id: channelId,
          account_name: `${appKey}_${Date.now()}`,
          connection_settings: connectionSettings
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create app account: ${response.status}, ${errorText}`);
      }

      const result = await response.json();

      // Close the dialog
      setBasicAuthDialogOpen(false);
      setPendingBasicAuthConnection(null);

      // Refresh apps list to show updated connection status
      await fetchApps(appSearchTerm, selectedCategory);
      
      // Clear loading state
      setConnectingApps(prev => ({ ...prev, [appKey]: false }));

    } catch (error) {
      console.error('Error creating basic auth connection:', error);
      alert('Failed to complete connection. Please check your credentials and try again.');
      
      // Clear loading state on error
      if (pendingBasicAuthConnection) {
        setConnectingApps(prev => ({ ...prev, [pendingBasicAuthConnection.appKey]: false }));
      }
    }
  };

  // Handle Basic Auth dialog close
  const handleBasicAuthDialogClose = () => {
    setBasicAuthDialogOpen(false);
    
    // Clear loading state if connection was cancelled
    if (pendingBasicAuthConnection) {
      setConnectingApps(prev => ({ ...prev, [pendingBasicAuthConnection.appKey]: false }));
      setPendingBasicAuthConnection(null);
    }
  };

  // Handle Odoo credential form submit
  const handleOdooConnect = async (connectionData: OdooConnectionData) => {
    if (!pendingOdooConnection) {
      console.error('No pending Odoo connection found');
      return;
    }

    try {
      const { appKey, appId } = pendingOdooConnection;

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }

      const encryptedApiKey = await encryptSecretForStorage(connectionData.apiKey);

      const connectionSettings = {
        provider: 'odoo',
        odoo_url: connectionData.odooUrl,
        database_name: connectionData.databaseName,
        username: connectionData.username,
        api_key: encryptedApiKey,
        connection_method: 'api_key',
      };

      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        },
        body: JSON.stringify({
          app_id: appId,
          channel_id: channelId,
          account_name: `${appKey}_${Date.now()}`,
          connection_settings: connectionSettings,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create app account: ${response.status}, ${errorText}`);
      }

      setOdooDialogOpen(false);
      setPendingOdooConnection(null);
      await fetchApps(appSearchTerm, selectedCategory);
      setConnectingApps(prev => ({ ...prev, [appKey]: false }));
    } catch (error) {
      console.error('Error creating Odoo connection:', error);
      alert('Failed to complete connection. Please check your credentials and try again.');
      if (pendingOdooConnection) {
        setConnectingApps(prev => ({ ...prev, [pendingOdooConnection.appKey]: false }));
      }
      throw error;
    }
  };

  const handleOdooDialogClose = () => {
    setOdooDialogOpen(false);
    if (pendingOdooConnection) {
      setConnectingApps(prev => ({ ...prev, [pendingOdooConnection.appKey]: false }));
      setPendingOdooConnection(null);
    }
  };

  return (
    <Box>
      {/* Search Bar with Category Filter */}
      {(showSearch || showCategoryFilter) && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-start',
          alignItems: 'center',
          mb: 2,
          width: '100%'
        }}>
          {/* Search and Filter Container */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
            {showSearch && (
              <TextField
                placeholder="Search apps..."
                value={appSearchTerm}
                onChange={(e) => setAppSearchTerm(e.target.value)}
                size="small"
                sx={{
                  width: 250,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                    borderRadius: 2,
                    borderColor: muiTheme.palette.divider,
                    '&:hover': {
                      borderColor: muiTheme.palette.grey[400]
                    },
                    '&.Mui-focused': {
                      borderColor: muiTheme.palette.primary.main
                    }
                  }
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: muiTheme.palette.text.secondary, mr: 1 }} />,
                  endAdornment: appSearchTerm && (
                    <Box 
                      component="span" 
                      sx={{ 
                        cursor: 'pointer',
                        color: muiTheme.palette.text.secondary,
                        '&:hover': { color: muiTheme.palette.text.primary }
                      }}
                      onClick={() => setAppSearchTerm('')}
                    >
                      ✕
                    </Box>
                  )
                }}
              />
            )}
            
            {/* Category Filter Dropdown */}
            {showCategoryFilter && (
              <FormControl 
                size="small"
                sx={{ 
                  minWidth: 200,
                }}
              >
                <InputLabel shrink>Category</InputLabel>
                <Select
                  label="Category"
                  value={categories.length > 0 ? selectedCategory : ''}
                  onChange={(e) => setSelectedCategory(e.target.value as string)}
                  displayEmpty
                  notched
                  renderValue={(selected) => {
                    if (!selected || selected === '') {
                      return 'All Categories';
                    }
                    return selected;
                  }}
                  MenuProps={{
                    disablePortal: false,
                    sx: {
                      zIndex: 9999,
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        marginTop: 1,
                      }
                    }
                  }}
                  sx={{
                    backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                  }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.length > 0 ? (
                    categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Loading categories...</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      )}

      {/* Loading State - Initial Load */}
      {loading && apps.length === 0 && (
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8
        }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Scrollable Container with Apps Grid */}
      <Box
        ref={scrollContainerRef}
        sx={{
          height: disableInternalScroll ? 'auto' : (height || (pageSize === 8 
            ? 'calc(100vh - 450px)' // For modal (AddSpaceModal) - accounts for modal header, search, footer
            : 'calc(100vh - 200px)')), // For full page (ChannelSettings) - increased height since no powered by text
          maxHeight: disableInternalScroll ? 'none' : (pageSize === 8 ? '450px' : '800px'),
          minHeight: '200px',
          overflowY: disableInternalScroll ? 'visible' : 'auto',
          overflowX: 'hidden',
          pr: disableInternalScroll ? 0 : 1,
          width: '100%',
          ...(disableInternalScroll ? {} : {
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
              '&:hover': {
                background: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              },
            },
          }),
        }}
      >
        {/* Apps Grid - 4 columns per row */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: selectionMode 
            ? 'repeat(auto-fill, minmax(200px, 240px))' // For selection mode (DataSourceStep)
            : 'repeat(4, 1fr)', // For settings mode (AppsTab)
          gap: 1.5,
          width: '100%',
          opacity: loading && apps.length === 0 ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pb: 2
        }}>
        {filteredApps.map((app) => {
          // Treat app as connected for display when we have real connection or optimistic “just connected” id
          const isEffectivelyConnected = app.isConnected || (optimisticConnectedAppId != null && app.id === optimisticConnectedAppId);
          return (
          <Paper
            key={app.id}
            elevation={selectionMode && selectedApp?.id === app.id ? 3 : 1}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: selectionMode && selectedApp?.id === app.id 
                ? `2px solid ${muiTheme.palette.primary.main}` 
                : `1px solid ${muiTheme.palette.divider}`,
              backgroundColor: muiTheme.palette.background.paper,
              cursor: selectionMode && isEffectivelyConnected ? 'pointer' : 'default',
              transition: 'all 0.2s ease-in-out',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: selectionMode ? '140px' : 'auto',
              position: 'relative',
              '&:hover': (selectionMode && isEffectivelyConnected) || !selectionMode ? {
                boxShadow: isDarkMode ? '0 4px 12px rgba(255,255,255,0.15)' : '0 4px 12px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)'
              } : {}
            }}
            onClick={() => selectionMode && isEffectivelyConnected && handleSelectApp(app)}
          >
            {/* Selection Indicator for selection mode */}
            {selectionMode && selectedApp?.id === app.id && (
              <Box
                key={`selection-indicator-${app.id}`}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                }}
              >
                <CheckCircle sx={{ color: muiTheme.palette.primary.main, fontSize: 20 }} />
              </Box>
            )}

            {/* Line 1: App Name + Connection Status + Category */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ flex: 1, pr: selectionMode && selectedApp?.id === app.id ? 3 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0 }}>
                    {app.app_name}
                  </Typography>
                  {isEffectivelyConnected && !selectionMode && (
                    <Chip
                      key={`connected-chip-${app.id}`}
                      label="Connected"
                      size="small"
                      sx={{
                        backgroundColor: muiTheme.palette.success.light + '20',
                        color: muiTheme.palette.success.dark,
                        fontSize: '0.55rem',
                        height: 16,
                        '& .MuiChip-label': {
                          px: 0.4
                        }
                      }}
                    />
                  )}
                  {/* Manage button: show connected account details in modal (right of app name) */}
                  {!selectionMode && app.isConnected && (
                    <Tooltip title="Manage">
                      <IconButton
                        size="small"
                        aria-label="Manage app connection"
                        onClick={(e) => {
                          e.stopPropagation();
                          setManageModalApp(app);
                        }}
                        sx={{
                          ml: 'auto',
                          color: muiTheme.palette.text.secondary,
                          '&:hover': { color: muiTheme.palette.primary.main, backgroundColor: muiTheme.palette.action.hover },
                        }}
                      >
                        <SettingsIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: muiTheme.palette.text.secondary,
                    fontSize: '0.7rem'
                  }}
                >
                  {app.category}
                </Typography>
              </Box>
              {!selectionMode && app.isConnected && app.app_key === 'jira' && app.connectionDetails && (() => {
                try {
                  const connectionSettings = typeof app.connectionDetails.connection_settings === 'string' 
                    ? JSON.parse(app.connectionDetails.connection_settings)
                    : app.connectionDetails.connection_settings;
                  const selectedProjects = connectionSettings?.selected_projects || [];
                  const projectCount = Array.isArray(selectedProjects) ? selectedProjects.length : 0;
                  
                  if (projectCount > 0) {
                    // Show count as badge with edit icon button
                    return (
                      <Box key={`jira-projects-${app.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={`${projectCount} ${projectCount === 1 ? 'project' : 'projects'}`}
                          size="small"
                          sx={{
                            backgroundColor: muiTheme.palette.primary.light + '15',
                            color: muiTheme.palette.primary.main,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            height: 24,
                            cursor: 'default',
                            '& .MuiChip-label': {
                              px: 1,
                              py: 0.5
                            }
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditJiraProjects(app);
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            color: muiTheme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: muiTheme.palette.primary.light + '20',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease-in-out',
                          }}
                          title="Edit projects"
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    );
                  } else {
                    // Show "Add projects" button when no projects are selected
                    return (
                      <Button
                        key={`jira-add-projects-${app.id}`}
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditJiraProjects(app);
                        }}
                        sx={{
                          minWidth: 'auto',
                          height: 28,
                          px: 1.5,
                          py: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          textTransform: 'none',
                          borderColor: muiTheme.palette.info.main,
                          color: muiTheme.palette.info.main,
                          backgroundColor: muiTheme.palette.info.light + '10',
                          borderStyle: 'dashed',
                          '&:hover': {
                            backgroundColor: muiTheme.palette.info.light + '20',
                            borderColor: muiTheme.palette.info.dark,
                            borderStyle: 'solid',
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 4px ${muiTheme.palette.info.main}20`,
                          },
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiButton-startIcon': {
                            marginRight: 0.5,
                            marginLeft: 0,
                          }
                        }}
                      >
                        Add projects
                      </Button>
                    );
                  }
                } catch (error) {
                  // Silently handle parsing errors
                }
                return null;
              })()}
            </Box>

            {/* Connection Status Chip for selection mode */}
            {selectionMode && app.isConnected && (
              <Chip
                key={`selection-connected-chip-${app.id}`}
                label="Connected"
                size="small"
                sx={{
                  backgroundColor: muiTheme.palette.success.light + '20',
                  color: muiTheme.palette.success.dark,
                  fontSize: '0.6rem',
                  height: 18,
                  mb: 1,
                  alignSelf: 'flex-start',
                  '& .MuiChip-label': {
                    px: 0.8
                  }
                }}
              />
            )}

            {/* Line 3: App Icon (left) + Connect/Disconnect Button (right) */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 'auto',
              pt: selectionMode ? 1 : 0
            }}>
              {/* App Icon */}
              <Box
                sx={{
                  width: selectionMode ? 36 : (pageSize == 8 ? 18 : 24),
                  height: selectionMode ? 36 : (pageSize == 8 ? 18 : 24),
                  borderRadius: 1,
                  backgroundColor: imageErrors[app.id] ? (isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50]) : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: selectionMode ? '1.1rem' : '0.8rem',
                  fontWeight: 'bold',
                  color: imageErrors[app.id] ? muiTheme.palette.text.secondary : 'transparent',
                  flexShrink: 0,
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {imageErrors[app.id] ? (
                  app.app_name.charAt(0).toUpperCase()
                ) : (
                  <img
                    src={app.app_key === 'odoo' ? (app.app_image || '/images/apps/odoo_crm.png') : app.app_image}
                    alt={`${app.app_name} icon`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '4px'
                    }}
                    onError={() => setImageErrors(prev => ({ ...prev, [app.id]: true }))}
                  />
                )}
              </Box>

              {/* Connect/Disconnect/Select Button */}
              {selectionMode ? (
                // Selection mode buttons
                (() => {
                  // Show COMING SOON for inactive apps (is_active: false)
                  if (!app.is_active) {
                    return (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled
                        sx={{
                          borderColor: muiTheme.palette.divider,
                          color: muiTheme.palette.text.disabled,
                          backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                          fontSize: '0.7rem',
                          py: 0.4,
                          px: 1.2,
                          minWidth: '80px',
                          cursor: 'not-allowed',
                          '&:disabled': {
                            backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                            color: muiTheme.palette.text.disabled,
                            borderColor: muiTheme.palette.divider
                          }
                        }}
                      >
                        COMING SOON
                      </Button>
                    );
                  }
                  
                  // Show CONNECT/SELECT for active apps (is_active: true); use isEffectivelyConnected so optimistic “just connected” shows SELECT immediately
                  return !isEffectivelyConnected ? (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={currentConnectingApps[app.app_key] || !isChannelAdmin}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConnect(app.app_key, app.id);
                      }}
                      sx={{
                        backgroundColor: muiTheme.palette.primary.main,
                        color: 'white',
                        fontSize: '0.7rem',
                        py: 0.4,
                        px: 1.2,
                        minWidth: '80px',
                        '&:hover': {
                          backgroundColor: muiTheme.palette.primary.dark,
                        },
                        // grey[300] is always light — unreadable with default disabled text in dark mode
                        '&:disabled': {
                          backgroundColor: isDarkMode
                            ? muiTheme.palette.grey[800]
                            : muiTheme.palette.grey[300],
                          color: isDarkMode
                            ? muiTheme.palette.grey[100]
                            : muiTheme.palette.text.disabled,
                        }
                      }}
                    >
                      {currentConnectingApps[app.app_key] ? (
                        <>
                          <CircularProgress size={12} sx={{ mr: 0.5, color: 'inherit' }} />
                          <Typography sx={{ fontSize: '0.65rem' }}>Connecting</Typography>
                        </>
                      ) : (
                        'CONNECT'
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant={selectedApp?.id === app.id ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectApp(app);
                      }}
                      sx={{
                        backgroundColor: selectedApp?.id === app.id ? muiTheme.palette.primary.main : 'transparent',
                        color: selectedApp?.id === app.id ? 'white' : muiTheme.palette.primary.main,
                        fontSize: '0.7rem',
                        py: 0.4,
                        px: 1.2,
                        minWidth: '70px',
                        borderColor: muiTheme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: selectedApp?.id === app.id ? muiTheme.palette.primary.dark : muiTheme.palette.primary.light + '20',
                          borderColor: muiTheme.palette.primary.main,
                        }
                      }}
                    >
                      {selectedApp?.id === app.id ? 'SELECTED' : 'SELECT'}
                    </Button>
                  );
                })()
              ) : (
                // Regular mode buttons
                (() => {
                  // Show COMING SOON for inactive apps (is_active: false)
                  if (!app.is_active) {
                    return (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled
                        sx={{
                          borderColor: muiTheme.palette.divider,
                          color: muiTheme.palette.text.disabled,
                          backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                          fontSize: pageSize == 8 ? '0.65rem' : '0.8rem',
                          py: 0.5,
                          px: 1.5,
                          cursor: 'not-allowed',
                          '&:disabled': {
                            backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                            color: muiTheme.palette.text.disabled,
                            borderColor: muiTheme.palette.divider
                          }
                        }}
                      >
                        COMING SOON
                      </Button>
                    );
                  }
                  
                  // Show CONNECT/DISCONNECT for active apps (is_active: true)
                  return isChannelAdmin ? (
                    <Button
                      variant={app.isConnected ? "outlined" : "contained"}
                      size="small"
                      disabled={currentConnectingApps[app.app_key] || disconnectingApps[app.app_key] || isChannelArchived}
                      onClick={() => app.isConnected ? handleDisconnect(app) : handleConnect(app.app_key, app.id)}
                      sx={{
                        backgroundColor: app.isConnected ? 'transparent' : muiTheme.palette.primary.main,
                        color: app.isConnected ? muiTheme.palette.error.main : 'white',
                        fontSize: pageSize == 8 ? '0.65rem' : '0.8rem',
                        py: 0.5,
                        px: 1.5,
                        borderColor: app.isConnected ? muiTheme.palette.error.main : 'transparent',
                        '&:hover': {
                          backgroundColor: app.isConnected ? muiTheme.palette.error.light + '20' : muiTheme.palette.primary.dark,
                          borderColor: app.isConnected ? muiTheme.palette.error.dark : 'transparent'
                        },
                        '&:disabled': {
                          backgroundColor: isDarkMode
                            ? muiTheme.palette.grey[800]
                            : muiTheme.palette.grey[300],
                          color: isDarkMode
                            ? muiTheme.palette.grey[100]
                            : muiTheme.palette.text.disabled,
                          borderColor: app.isConnected
                            ? (isDarkMode ? muiTheme.palette.divider : muiTheme.palette.grey[400])
                            : isDarkMode
                              ? 'transparent'
                              : muiTheme.palette.grey[300],
                        }
                      }}
                    >
                      {currentConnectingApps[app.app_key] ? (
                        <>
                          <CircularProgress size={14} sx={{ mr: 0.5, color: 'inherit' }} />
                          Connecting...
                        </>
                      ) : disconnectingApps[app.app_key] ? (
                        <>
                          <CircularProgress size={14} sx={{ mr: 0.5, color: 'inherit' }} />
                          Disconnecting...
                        </>
                      ) : (
                        app.isConnected ? 'Disconnect' : 'Connect'
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled
                      sx={{
                        borderColor: muiTheme.palette.divider,
                        color: muiTheme.palette.text.disabled,
                        backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                        fontSize: '0.8rem',
                        py: 0.5,
                        px: 1.5,
                        cursor: 'not-allowed',
                        '&:disabled': {
                          backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                          color: muiTheme.palette.text.disabled,
                          borderColor: muiTheme.palette.divider
                        }
                      }}
                    >
                      {app.isConnected ? 'Disconnect' : 'Connect'}
                    </Button>
                  );
                })()
              )}
            </Box>
          </Paper>
          );
        })}
        </Box>

      </Box>

      {/* Empty state */}
      {filteredApps.length === 0 && !loading && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 6,
          color: 'text.secondary'
        }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            No apps found
          </Typography>
          <Typography variant="body2">
            Try adjusting your search terms or browse all available apps.
          </Typography>
        </Box>
      )}

      {/* Jira Project Selection Dialog */}
      <Dialog
        open={jiraProjectDialogOpen}
        onClose={handleJiraProjectSelectionCancel}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: '1500 !important',
          '& .MuiBackdrop-root': {
            zIndex: '1499 !important',
          },
          '& .MuiDialog-paper': {
            zIndex: '1500 !important',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          Select Jira Projects
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
            Please select the Jira projects you want to connect to this channel. You can select multiple projects.
          </Typography>
          
          {loadingJiraProjects ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading projects...
              </Typography>
            </Box>
          ) : jiraProjects.length === 0 ? (
            <Box sx={{ py: 2 }}>
              <Alert severity="info">
                No projects found. You can still connect without selecting projects.
              </Alert>
            </Box>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="jira-projects-label">Projects</InputLabel>
              <Select
                labelId="jira-projects-label"
                id="jira-projects-select"
                multiple
                value={selectedJiraProjects.map(p => p.id)}
                onChange={(e) => {
                  const selectedIds: string[] = typeof e.target.value === 'string' 
                    ? e.target.value.split(',') 
                    : e.target.value as string[];
                  // Convert IDs back to project objects
                  const selectedProjects = jiraProjects.filter((p: JiraProject) => selectedIds.includes(p.id));
                  setSelectedJiraProjects(selectedProjects);
                }}
                input={<OutlinedInput label="Projects" />}
                renderValue={(selectedIds: unknown) => {
                  const ids = selectedIds as string[];
                  if (ids.length === 0) {
                    return 'No projects selected';
                  }
                  return ids
                    .map((projectId: string) => {
                      const project = jiraProjects.find((p: JiraProject) => p.id === projectId);
                      return project ? `${project.name} (${project.key})` : projectId;
                    })
                    .join(', ');
                }}
                MenuProps={{
                  disablePortal: false,
                  sx: {
                    zIndex: 9999,
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      marginTop: 1,
                    }
                  }
                }}
              >
                {jiraProjects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    <Checkbox checked={selectedJiraProjects.some(p => p.id === project.id)} />
                    <ListItemText 
                      primary={`${project.name} (${project.key})`}
                      secondary={project.projectTypeKey}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleJiraProjectSelectionCancel}
            variant="text"
            disabled={loadingJiraProjects}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleJiraProjectSelectionConfirm}
            variant="contained"
            color="primary"
            disabled={loadingJiraProjects}
            sx={{ 
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&:disabled': {
                backgroundColor: isDarkMode
                  ? muiTheme.palette.grey[800]
                  : muiTheme.palette.grey[300],
                color: isDarkMode ? muiTheme.palette.grey[100] : undefined,
              }
            }}
          >
            {loadingJiraProjects ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unified OAuth Connection Dialog */}
      <OAuthConnectionDialog
        open={oauthDialogOpen}
        provider={selectedOAuthProvider}
        onClose={handleOAuthDialogClose}
        onConnect={handleOAuthConnect}
      />
      
      <BasicAuthConnectionDialog
        open={basicAuthDialogOpen}
        appName={pendingBasicAuthConnection?.appName || 'Freshdesk'}
        onClose={handleBasicAuthDialogClose}
        onConnect={handleBasicAuthConnect}
      />
      <OdooConnectionDialog
        open={odooDialogOpen}
        appName={pendingOdooConnection?.appName || 'Odoo'}
        onClose={handleOdooDialogClose}
        onConnect={handleOdooConnect}
      />

      {/* App account manage modal: profile card with connected account details */}
      <AppAccountManageModal
        open={!!manageModalApp}
        onClose={() => setManageModalApp(null)}
        app={manageModalApp}
        connectionDetails={manageModalApp?.connectionDetails ?? null}
        imageError={manageModalApp ? !!imageErrors[manageModalApp.id] : false}
      />
    </Box>
  );
};

export default AppsGrid;
